import { cn } from '../../lib/cn';

/**
 * docs/02-DESIGN-SYSTEM.md §4.1.
 *
 * A spinner is a status, not decoration: it carries `role="status"` and a
 * caller-supplied label so a screen reader announces that something is
 * happening. The label is user-visible text and comes from the i18n bundle
 * (CLAUDE.md rule 2).
 *
 * §6 — `animate-spin` is neutralised under `prefers-reduced-motion` by the
 * global rule in base.css, leaving a static ring. Prefer `Skeleton` for lists:
 * §4.1 requires skeletons for every async list so there is no layout shift.
 */
export interface SpinnerProps {
  /** Announced while loading, e.g. "იტვირთება…". */
  label: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const SIZES = { sm: 'h-4 w-4 border-2', md: 'h-6 w-6 border-2', lg: 'h-10 w-10 border-[3px]' };

export function Spinner({ label, size = 'md', className }: SpinnerProps) {
  return (
    <span role="status" className={cn('inline-flex items-center gap-2', className)}>
      <span
        aria-hidden="true"
        className={cn('animate-spin rounded-full border-navy-200 border-t-navy-600', SIZES[size])}
      />
      <span className="sr-only">{label}</span>
    </span>
  );
}
